import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import {
  GeminiBotaxService,
  BotoxIntensity,
  BotoxTreatment
} from '../../services/gemini-botax-service.service';

interface EditedImage {
  id: string;
  originalUrl: string;
  editedUrl: string;
  prompt: string;
  timestamp: number;
}

interface ProcessingState {
  isProcessing: boolean;
  error: string | null;
}

interface TreatmentOption {
  id: BotoxTreatment;
  label: string;
}

interface IntensityOption {
  value: BotoxIntensity;
  label: string;
}

@Component({
  selector: 'app-botax-vison',
  templateUrl: './botax-vison.component.html',
  styleUrls: ['./botax-vison.component.scss']
})
export class BotaxVisonComponent implements OnDestroy {
  @Input() showHeader = true;
  @Input() embedded = false;
  @Input() showMobileFooter = true;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  webcamStream: MediaStream | null = null;
  webcamError: string | null = null;
  webcamActive = false;

  @ViewChild('webcamVideo') webcamVideo?: ElementRef<HTMLVideoElement>;

  /**
   * Keeping the original variable so existing history logic remains intact.
   * Now it becomes the generated label used for the history entry.
   */
  prompt = '';

  history: EditedImage[] = [];
  processing: ProcessingState = { isProcessing: false, error: null };
  currentResult: string | null = null;

  // --- New: Treatment + Intensity controls ---
  readonly treatments: TreatmentOption[] = [
    { id: 1, label: 'Glabellar “11” Lines (Frown Lines)' },
    { id: 2, label: 'Forehead Horizontal Lines' },
    { id: 3, label: 'Crow’s Feet (Outer Eye Lines)' },
    { id: 4, label: 'Masseter (Jaw Softening/Slimming)' },
    { id: 5, label: 'Lip Flip (Upper Lip)' }
  ];

  readonly intensityOptions: IntensityOption[] = [
    { value: 1, label: 'Subtle' },
    { value: 2, label: 'Moderate (Default)' },
    { value: 3, label: 'Strong' }
  ];

  /**
   * Default intensity is 2 for EACH treatment, as requested.
   * This also preserves the intensity per treatment as the user switches treatments.
   */
  intensityByTreatment: Record<number, BotoxIntensity> = {
    1: 2,
    2: 2,
    3: 2,
    4: 2,
    5: 2
  };

  selectedTreatment: BotoxTreatment = 1;
  selectedIntensity: BotoxIntensity = 2;

  constructor(private geminiBotax: GeminiBotaxService) {}

  ngOnDestroy(): void {
    this.stopWebcam();
    this.revokePreviewUrl();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!/^image\//i.test(file.type)) {
      this.processing = { isProcessing: false, error: 'Please upload a valid image file.' };
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.processing = { isProcessing: false, error: 'PNG, JPG or WebP only (max 10MB).' };
      return;
    }

    this.selectedFile = file;
    this.setPreviewUrl(URL.createObjectURL(file));
    this.currentResult = null;
    this.webcamError = null;
    this.processing = { isProcessing: false, error: null };

    // Ensure bindings are consistent on fresh upload
    this.syncIntensityFromTreatment();
  }

  onTreatmentChange(): void {
    this.syncIntensityFromTreatment();
  }

  onIntensityChange(next: any): void {
    const n = Number(next);
    const clamped: BotoxIntensity = (n <= 1 ? 1 : n === 2 ? 2 : 3) as BotoxIntensity;

    this.selectedIntensity = clamped;
    this.intensityByTreatment[this.selectedTreatment] = clamped;
  }

  async handleEdit(): Promise<void> {
    if (!this.selectedFile) return;

    this.processing = { isProcessing: true, error: null };

    try {
      const base64DataUrl = await this.fileToBase64(this.selectedFile);

      // Keep a readable entry for history, mapped to the server signature (treatment + intensity)
      this.prompt = this.buildHistoryLabel(this.selectedTreatment, this.selectedIntensity);

      const editedBase64 = await this.geminiBotax.applyTreatment(
        base64DataUrl,
        this.selectedFile.type || 'image/png',
        this.selectedTreatment,
        this.selectedIntensity
      );

      this.currentResult = editedBase64;

      const newEntry: EditedImage = {
        id: crypto.randomUUID(),
        originalUrl: this.previewUrl || '',
        editedUrl: editedBase64,
        prompt: this.prompt,
        timestamp: Date.now()
      };

      this.history = [newEntry, ...this.history];
    } catch (err: any) {
      this.processing = { isProcessing: false, error: err?.message || 'Failed to apply Botox treatment.' };
      return;
    } finally {
      this.processing = { ...this.processing, isProcessing: false };
    }
  }

  clearSession(): void {
    this.selectedFile = null;
    this.currentResult = null;
    this.prompt = '';
    this.webcamError = null;
    this.processing = { isProcessing: false, error: null };
    this.setPreviewUrl(null);

    // Reset defaults: intensity 2 for each treatment
    this.intensityByTreatment = { 1: 2, 2: 2, 3: 2, 4: 2, 5: 2 };
    this.selectedTreatment = 1;
    this.selectedIntensity = 2;
  }

  async startWebcam(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.webcamError = 'Webcam is not supported in this browser.';
      return;
    }

    this.webcamError = null;
    this.processing = { ...this.processing, error: null };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });

      this.stopWebcam();
      this.webcamStream = stream;
      this.webcamActive = true;

      setTimeout(() => {
        const video = this.webcamVideo?.nativeElement;
        if (!video || !this.webcamStream) return;

        video.srcObject = this.webcamStream;
        video.muted = true;
        video.playsInline = true;
        void video.play().catch(() => {
          this.webcamError = 'Camera started but playback was blocked. Tap the video and try again.';
        });
      });
    } catch {
      this.webcamActive = false;
      this.webcamError = 'Unable to access the webcam. Check browser permissions and try again.';
    }
  }

  stopWebcam(): void {
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach((track) => track.stop());
      this.webcamStream = null;
    }

    const video = this.webcamVideo?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }

    this.webcamActive = false;
  }

  async captureFromWebcam(): Promise<void> {
    const video = this.webcamVideo?.nativeElement;
    if (!video || !this.webcamStream) {
      this.webcamError = 'Start the webcam before capturing a photo.';
      return;
    }

    if (!video.videoWidth || !video.videoHeight) {
      this.webcamError = 'Camera is still warming up. Try capture again.';
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      this.webcamError = 'Could not capture image from webcam.';
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });

    if (!blob) {
      this.webcamError = 'Failed to generate a photo from the webcam.';
      return;
    }

    const cameraFile = new File([blob], `webcam-${Date.now()}.png`, { type: 'image/png' });
    this.selectedFile = cameraFile;
    this.setPreviewUrl(URL.createObjectURL(cameraFile));
    this.currentResult = null;
    this.processing = { isProcessing: false, error: null };
    this.webcamError = null;
    this.syncIntensityFromTreatment();
    this.stopWebcam();
  }

  deleteHistoryItem(id: string): void {
    this.history = this.history.filter((item) => item.id !== id);
  }

  viewHistoryItem(url: string): void {
    this.currentResult = url;
  }

  // --------------------------
  // Helpers (used by template)
  // --------------------------
  intensityWord(level: BotoxIntensity): string {
    if (level === 1) return 'subtle';
    if (level === 2) return 'moderate';
    return 'strong';
  }

  treatmentLabel(t: BotoxTreatment): string {
    return this.treatments.find((x) => x.id === t)?.label || `Treatment ${t}`;
  }

  // --------------------------
  // Private internals
  // --------------------------
  private syncIntensityFromTreatment(): void {
    const saved = this.intensityByTreatment[this.selectedTreatment];
    this.selectedIntensity = (saved ?? 2) as BotoxIntensity;
  }

  private buildHistoryLabel(treatment: BotoxTreatment, intensity: BotoxIntensity): string {
    return `${this.treatmentLabel(treatment)} • Intensity ${intensity} (${this.intensityWord(intensity)})`;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  private setPreviewUrl(url: string | null): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = url;
  }

  private revokePreviewUrl(): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
}